import React, { useEffect, useRef } from 'react';
import { GlobalStyles, Paper, Typography, useTheme } from '@mui/material';
import JSONEditor from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import { FieldValues, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { isEqual } from 'lodash-es';

interface JsonEditorProps<TFieldValues extends FieldValues = any> {
  name: string; // the form field name
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  setError?: UseFormSetError<TFieldValues>;
  clearErrors?: UseFormClearErrors<TFieldValues>;
  'data-testid'?: string;
  enableDndInsert?: boolean; // when true, allow dropping text to insert at cursor
  height?: number | string; // editor height (e.g., '70vh')
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
  name,
  value,
  onChange,
  error,
  disabled = false,
  setError,
  clearErrors,
  'data-testid': testId,
  enableDndInsert = false,
  height = '230px',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<JSONEditor | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (containerRef.current && !editorRef.current) {
      editorRef.current = new JSONEditor(containerRef.current, {
        mode: 'code',
        statusBar: true,
        mainMenuBar: true,
        navigationBar: false,
        indentation: 2,
        history: true,
        // onChange: () => {
        //   try {
        //     const json = editorRef.current?.get();
        //     clearErrors(name);
        //     onChange(json ?? {});
        //   } catch (e: any) {
        //     setError(name, { type: 'manual', message: e.message || 'Invalid JSON' });
        //   }
        // },
        onChangeText: (text) => {
          try {
            const json = JSON.parse(text);
            if (clearErrors) {
              clearErrors(name);
            }
            onChange(json);
          } catch (e: any) {
            if (setError) {
              setError(name, {
                type: 'manual',
                message: e.message || 'Invalid JSON',
              });
            }
          }
        },
      });

      // Set initial value
      editorRef.current.set(value ?? {});

      if (disabled) {
        editorRef.current.setMode('view');
      }
    }

    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
    };
  }, [name, onChange, setError, clearErrors, disabled]);

  useEffect(() => {
    if (editorRef.current) {
      try {
        const current = editorRef.current.get();
        // Only update if values differ AND editor content isn't "in-progress typing"
        if (!isEqual(current, value)) {
          editorRef.current.update(value ?? {});
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [value]);

  return (
    <>
      <GlobalStyles
        styles={{
          '.jsoneditor, .jsoneditor-outer': {
            // border: '1px solid !important',
            borderColor: `${theme.palette.divider} !important`,
          },
          '.jsoneditor-poweredBy': { display: 'none !important' },
          '.jsoneditor-menu': {
            backgroundColor: `${theme.palette.primary.main} !important`,
          },
          '.jsoneditor-menu button.jsoneditor-sort': {
            display: 'none !important',
          },
          '.ace-jsoneditor.ace_editor, .ace_content, .ace_gutter, .ace_gutter-active-line, .jsoneditor-statusbar': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          '.ace-jsoneditor .ace_variable': {
            color: `${theme.palette.text.primary} !important`,
          },
          '.ace_active-line': {
            backgroundColor: `${theme.palette.action.selected} !important`,
          },
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label
          htmlFor={testId}
          style={{
            fontWeight: 500,
            marginBottom: 8,
            color: error ? 'red' : '#2b2b2b',
            fontSize: 14,
          }}
        ></label>
        <Paper
          sx={{
            height,
            backgroundColor: '#f8f5eb',
            boxShadow: 'none',
          }}
        >
          <div
            id={testId}
            ref={containerRef}
            style={{
              height: '100%',
              fontFamily: `"Monaco","Menlo","Ubuntu Mono",monospace`,
              fontSize: '15px',
            }}
            onDragOver={(e) => {
              if (!enableDndInsert) return;
              e.preventDefault();
            }}
            onDrop={(e) => {
              if (!enableDndInsert) return;
              e.preventDefault();
              const text = e.dataTransfer.getData('text/field-name') || e.dataTransfer.getData('text/plain');
              if (text && editorRef.current && (editorRef.current as any).aceEditor) {
                // Insert a JSON-safe string value at cursor
                const insert = `"${text}"`;
                (editorRef.current as any).aceEditor.insert(insert);
              }
            }}
          />
        </Paper>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: -1 }}>
            {error}
          </Typography>
        )}
      </div>
    </>
  );
};
